import { Modal } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { logoutRequest } from '../../../../redux/actions/login';
import auth from '../../../../utils/auth';
import { useMergeState } from '../../../../utils/hooks';
import { toastrSuccess } from '../../../../utils/toastNotification';
import NormalButton from '../../buttons/normalButton';
import ChangePasswordStep from '../../ui/changePassword';
import ForgotPasswordStep from '../../ui/forgotPasswordStep';
import {
  changePassword,
  completePassword,
  getChangePassordForm, getForgotPasswordForm, getSchema, triggerResendOTP, triggerSendOTP,
} from './helper';
import './style.scss';


const ChangePasswordModal = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [state, setState] = useMergeState({
    isChangePassword: true,
    loading: false, // for submit button
    startTime: '',
    isCountDown: false,
    forgotPasswordLoading: false,
    resendCodeLoading: false,
  });
  const changePasswordSchema = getSchema(true);
  const changePasswordForm = getChangePassordForm(changePasswordSchema);

  const forgotPasswordSchema = getSchema(false);
  const forgotPasswordForm = getForgotPasswordForm(forgotPasswordSchema);

  const onCancel = () => {
    props.onCancel();
  };

  const backToLogin = () => {
    dispatch(logoutRequest());
    history.push('/login');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setState({ loading: true });
    changePasswordForm.clearErrors(['newPassword', 'currentPassword']);
    forgotPasswordForm.clearErrors(['newPassword', 'verificationCode']);
    if (changePasswordForm.getValues('newPassword').length < 6 && state.isChangePassword) {
      changePasswordForm.setError('newPassword', { message: 'The new password must contain at least 6 digits.' }, { shouldFocus: true });
      setState({ loading: false });
      return;
    }
    if (forgotPasswordForm.getValues('newPassword').length < 6 && !state.isChangePassword) {
      forgotPasswordForm.setError('newPassword', { message: 'The new password must contain at least 6 digits.' }, { shouldFocus: true });
      setState({ loading: false });
      return;
    }
    if (changePasswordForm.getValues('currentPassword') === changePasswordForm.getValues('newPassword') && state.isChangePassword) {
      changePasswordForm.setError('newPassword', { message: 'The new password cannot be the same as the current one.' }, { shouldFocus: true });
      setState({ loading: false });
      return;
    }
    if (state.isChangePassword) {
      const changedPassword = await changePassword(changePasswordForm.getValues());
      if (!changedPassword?.isSuccess) {
        changePasswordForm.setError('currentPassword', { message: 'Current password is incorrect.' }, { shouldFocus: true });
        setState({ loading: false });
        return;
      }
    } else {
      const completedPassword = await completePassword({ email: auth.getLoginData()?.email, ...forgotPasswordForm.getValues() });
      if (!completedPassword?.isSuccess) {
        forgotPasswordForm.setError('verificationCode', { message: 'Verification code is incorrect.' }, { shouldFocus: true });
        setState({ loading: false });
        return;
      }
    }
    backToLogin();
    setState({ loading: false });
    toastrSuccess('Password changed successfully');
    onCancel();
  };

  const onForgotPasswordClick = async () => {
    setState({ forgotPasswordLoading: true });
    const sendOTP = await triggerSendOTP(auth.getLoginData()?.email);
    if (!sendOTP?.isSuccess) {
      changePasswordForm.setError('newPassword', { message: sendOTP?.message });
      setState({ forgotPasswordLoading: false });
      return;
    }
    setState({ isChangePassword: !state.isChangePassword, forgotPasswordLoading: false });
  };

  const onSendCodeAgain = async () => {
    setState({ resendCodeLoading: true });
    const resendCode = await triggerResendOTP(auth.getLoginData()?.id);
    if (!resendCode.isSuccess) {
      forgotPasswordForm.setError('newPassword', { message: resendCode.message });
      setState({ loading: false });
    }
    setState({ startTime: moment().format(), isCountDown: true, resendCodeLoading: false });
  };

  const onStopCountDown = () => {
    setState({ startTime: '', isCountDown: false });
  };

  useEffect(() => {
    setState({ isChangePassword: true });
    changePasswordForm.reset({
      currentPassword: '',
      newPassword: '',
    });
    forgotPasswordForm.reset({
      verificationCode: '',
      newPassword: '',
    });
  }, [props.visible]);

  return (
    <Modal
      className="change-password-modal"
      title="Change password"
      visible={props.visible}
      width={394}
      maskClosable={props.maskClosable}
      onCancel={onCancel}
      footer={[
        <NormalButton
          key="submit"
          text="Change password"
          type="primary"
          loading={state.loading}
          disabled={state.isChangePassword
            ? !changePasswordForm?.formState?.isValid
            : !forgotPasswordForm?.formState?.isValid
            }
          onClick={onSubmit}
        />,
      ]}
    >
      {state.isChangePassword
        ? (
          <ChangePasswordStep
            form={changePasswordForm}
            onSubmit={onSubmit}
            onForgotPasswordClick={onForgotPasswordClick}
            forgotPasswordLoading={state.forgotPasswordLoading}
          />
        )
        : (
          <ForgotPasswordStep
            form={forgotPasswordForm}
            onSubmit={onSubmit}
            onSendCodeAgain={onSendCodeAgain}
            onStopCountDown={onStopCountDown}
            startTime={state.startTime}
            isCountDown={state.isCountDown}
            resendCodeLoading={state.resendCodeLoading}
          />
        ) }
    </Modal>
  );
};

ChangePasswordModal.defaultProps = {
  visible: false,
  maskClosable: false,

  onCancel: () => {},
};

ChangePasswordModal.propTypes = {
  visible: PropTypes.bool,
  maskClosable: PropTypes.bool,

  onCancel: PropTypes.func,
};

export default ChangePasswordModal;
